import dayjs from 'dayjs'
import { VITAL_FIELDS, InterventionStatus, type StockLot, type Vitals, type VitalKey, type Intervention } from '../types/fieldPost'

export type LotStatus = 'negative' | 'expired' | 'expiring' | 'ok'

export function addDays(isoDate: string, days: number): string {
    const d = new Date(`${isoDate}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() + days)
    return d.toISOString().slice(0, 10)
}

export function todayIso(now: Date = new Date()): string {
    return dayjs(now).format('YYYY-MM-DD')
}

export function lotStatus(lot: Pick<StockLot, 'qty_on_hand' | 'expires_on'>, today: string, expiringDays = 60): LotStatus {
    if (Number(lot.qty_on_hand) < 0) return 'negative'
    if (Number(lot.qty_on_hand) === 0) return 'ok'
    if (lot.expires_on && lot.expires_on < today) return 'expired'
    if (lot.expires_on && lot.expires_on <= addDays(today, expiringDays)) return 'expiring'
    return 'ok'
}

export function defaultLotFor<T extends Pick<StockLot, 'item_id' | 'qty_on_hand' | 'expires_on'>>(lots: T[], itemId: string, today: string): T | undefined {
    const usable = (l: T) => Number(l.qty_on_hand) > 0 && !(l.expires_on && l.expires_on < today) ? 0 : 1
    return lots
        .filter(l => l.item_id === itemId)
        .sort((a, b) => usable(a) - usable(b) || (a.expires_on ?? '9999-12-31').localeCompare(b.expires_on ?? '9999-12-31'))[0]
}

type CompletionValues = Pick<Intervention, 'reason_id' | 'disposition_id' | 'discharged_at' | 'nurse_id' | 'physician_id'>

export function missingForCompletion(values: CompletionValues, status: InterventionStatus): string[] {
    const missing: string[] = []
    if (status === InterventionStatus.completed) {
        if (!values.reason_id) missing.push('reason_id')
        if (!values.disposition_id) missing.push('disposition_id')
        if (!values.discharged_at) missing.push('discharged_at')
        if (!values.nurse_id && !values.physician_id) missing.push('team')
    } else if (status === InterventionStatus.refusedCare) {
        if (!values.discharged_at) missing.push('discharged_at')
    }
    return missing
}

export function sanitizeVitals(input: Record<string, unknown>): Vitals {
    const out: Vitals = {}
    for (const field of VITAL_FIELDS) {
        const raw = input[field.key]
        if (raw === null || raw === undefined) continue
        let value: number
        if (typeof raw === 'number') {
            value = raw
        } else {
            const s = String(raw).trim()
            if (!s) continue
            value = Number(s.replace(',', '.'))
        }
        if (Number.isFinite(value)) out[field.key] = value
    }
    return out
}

export function nonNumericVitals(input: Record<string, unknown>): VitalKey[] {
    const parsed = sanitizeVitals(input)
    return VITAL_FIELDS
        .filter(f => String(input[f.key] ?? '').trim() !== '' && parsed[f.key] === undefined)
        .map(f => f.key)
}

export function vitalErrors(vitals: Vitals): VitalKey[] {
    return VITAL_FIELDS
        .filter(f => vitals[f.key] !== undefined && (vitals[f.key]! < f.min || vitals[f.key]! > f.max))
        .map(f => f.key)
}

export function elapsedMinutes(fromIso: string, now: Date): number {
    return Math.max(0, Math.floor((now.getTime() - new Date(fromIso).getTime()) / 60000))
}

export function ageFromBirthDate(birthDate: string | null, now: Date): number | null {
    if (!birthDate) return null
    const birth = new Date(`${birthDate}T00:00:00Z`)
    let age = now.getUTCFullYear() - birth.getUTCFullYear()
    const beforeBirthday = now.getUTCMonth() < birth.getUTCMonth()
        || (now.getUTCMonth() === birth.getUTCMonth() && now.getUTCDate() < birth.getUTCDate())
    if (beforeBirthday) age -= 1
    return age
}

export function toLocalInput(iso: string | null): string {
    return iso ? dayjs(iso).format('YYYY-MM-DDTHH:mm') : ''
}

export function fromLocalInput(value: string): string | null {
    return value ? dayjs(value).toISOString() : null
}

export function formatQty(n: number): string {
    return String(Number(Number(n).toFixed(2)))
}
